import { PhonelinkRounded } from "@mui/icons-material"
import React from "react"

import { AppleSvg } from "../components/AppleSvg"
import { DockerSvg } from "../components/DockerSvg"
import { LinuxSvg } from "../components/LinuxSvg"
import { WindowsSvg } from "../components/WindowsSvg"
import { formatFileSize } from "./formatting-utils"
import { isServer } from "./utils"

export interface GitHubRelease {
  assets: {
    browser_download_url: string
    name: string
    size: number
  }[]
  body: string
  published_at: string
  tag_name: string
}

export type IconType = "windows" | "mac" | "linux" | "docker" | "web" | "android"

export interface DownloadItem {
  downloadUrl: string
  icon: IconType
  iconStyle?: { height: number; width: number }
  isExternalLink?: boolean
  platform?: string
  size?: string
  title: string
  version: string
}

export interface Release {
  body: string
  downloads: DownloadItem[]
  publishedAt: string
  version: string
}

export type PlatformDownload = {
  arch?: PlatformArch
  icon: IconType
  iconStyle?: { height: number; width: number }
  isWeb: boolean
  label: string
  platformId: PlatformId | "other"
  url: string
}

type PlatformId = "windows" | "mac" | "linux"
type PlatformArch = "arm64" | "x64"

export const getIconComponent = (iconType: IconType, style?: { height: number; width: number }) => {
  const defaultStyle = style || { height: 32, width: 32 }

  switch (iconType) {
    case "windows":
      return React.createElement(WindowsSvg, { style: defaultStyle })
    case "mac":
      return React.createElement(AppleSvg, { style: defaultStyle })
    case "linux":
      return React.createElement(LinuxSvg, { style: defaultStyle })
    case "docker":
      return React.createElement(DockerSvg, { style: defaultStyle })
    case "web":
      return React.createElement(PhonelinkRounded, { style: defaultStyle })
    case "android":
      return React.createElement(PhonelinkRounded, { style: defaultStyle })
    default:
      return React.createElement(PhonelinkRounded, { style: defaultStyle })
  }
}

export const getPlatformInfo = (
  filename: string,
  size: number
): {
  arch: PlatformArch
  icon: IconType
  iconStyle: { height: number; width: number }
  platform: string
  platformId: PlatformId
  title: string
} | null => {
  if (filename.includes(".blockmap") || filename.includes(".yml")) return null

  if (filename.includes(".exe")) {
    return {
      arch: "x64",
      icon: "windows",
      iconStyle: { height: 32, width: 32 },
      platform: `Windows 10/11 x64 · ${formatFileSize(size)}`,
      platformId: "windows",
      title: "Windows",
    }
  }
  if (
    filename.includes("darwin") ||
    filename.includes("mac") ||
    filename.includes("-stapled.dmg")
  ) {
    const arch = filename.includes("arm64") ? "arm64" : "x64"
    return {
      arch,
      icon: "mac",
      iconStyle: { height: 32, width: 32 },
      platform: `${arch} · ${formatFileSize(size)}`,
      platformId: "mac",
      title: "Mac",
    }
  }
  if (filename.includes(".deb")) {
    return {
      arch: "x64",
      icon: "linux",
      iconStyle: { height: 32, width: 32 },
      platform: `Debian x64 · ${formatFileSize(size)}`,
      platformId: "linux",
      title: "Linux",
    }
  }
  return null
}

export const fetchLatestRelease = async () => {
  const response = await fetch(
    "https://api.github.com/repos/privatefolio/privatefolio/releases/latest"
  )
  return response.json() as Promise<GitHubRelease>
}

export const fetchReleases = async (page: number, perPage = 3) => {
  const response = await fetch(
    `https://api.github.com/repos/privatefolio/privatefolio/releases?page=${page}&per_page=${perPage}`
  )
  const releases: GitHubRelease[] = await response.json()

  if (!isServer) console.log("Releases", releases)

  return releases.map((release) => {
    const downloads = release.assets
      .map((asset) => {
        const platformInfo = getPlatformInfo(asset.name, asset.size)
        if (!platformInfo) return null

        return {
          ...platformInfo,
          downloadUrl: asset.browser_download_url,
          size: formatFileSize(asset.size),
          version: release.tag_name,
        } as DownloadItem
      })
      .filter((item): item is DownloadItem => item !== null)

    downloads.sort((a, b) => a.title.localeCompare(b.title))

    return {
      body: release.body,
      downloads,
      publishedAt: release.published_at,
      version: release.tag_name,
    }
  })
}

export function getPlatformId() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : ""

  // Check for mobile first
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return "mobile"
  }

  if (ua.includes("Win")) return "windows"
  if (ua.includes("Mac")) return "mac"
  if (ua.includes("Linux")) return "linux"
  return "other"
}

export function getPlatformArch(): PlatformArch {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : ""
  if (ua.includes("arm64")) return "arm64"
  return "x64"
}

export async function getPlatformDownloads(): Promise<PlatformDownload[]> {
  const release = await fetchLatestRelease()

  if (!release || !release.assets) {
    console.error("No release or assets found", release)
    return []
  }

  const downloads: PlatformDownload[] = []

  const platformMap: Record<PlatformId, { icon: IconType; label: string }> = {
    linux: { icon: "linux", label: "Download for Linux" },
    mac: { icon: "mac", label: "Download for macOS" },
    windows: { icon: "windows", label: "Download for Windows" },
  }

  for (const asset of release.assets) {
    const platformInfo = getPlatformInfo(asset.name, asset.size)
    if (!platformInfo) continue

    const platformConfig = platformMap[platformInfo.platformId]
    if (!platformConfig) continue

    downloads.push({
      arch: platformInfo.arch,
      icon: platformConfig.icon,
      iconStyle: { height: 24, width: 24 },
      isWeb: false,
      label: platformConfig.label,
      platformId: platformInfo.platformId,
      url: asset.browser_download_url,
    })
  }

  return downloads
}

export function selectPlatformDownload(downloads: PlatformDownload[]): PlatformDownload | null {
  if (downloads.length === 0) return null

  const platformId = getPlatformId()
  const platformArch = getPlatformArch()

  // Find exact match (platform + arch)
  const exactMatch = downloads.find((d) => d.platformId === platformId && d.arch === platformArch)
  if (exactMatch) return exactMatch

  // Fallback: find any match for the platform
  const platformMatch = downloads.find((d) => d.platformId === platformId)
  if (platformMatch) return platformMatch

  return null
}
